<script lang="ts" setup>
import { onMounted, onUnmounted, ref } from 'vue';
import { Dibs } from '../../../../types/dibs';
import { CheckoutStages, CheckoutSteps } from '../../services/models/checkout.models';
import { useNetsEasyCheckoutStore } from '../../stores/netsEasyCheckoutStore';
import { Log } from '../../utils/API/Logging/LoggingAPI';
import { createPayment } from '../../utils/API/NetsEasy/netsEasyAPI';
import { delayForOrderPlacementTesting } from '../../utils/checkoutHelpers';
import { useShoppingCartStore } from '../shoppingCart/stores/shoppingCartStore';
import { useCheckoutStateStore } from './stores/checkoutStateStore';
import { useCustomerInformationStore } from './stores/customerInformationStore';
import { useDeliveryInformationStore } from './stores/deliveryInformationStore';

const LANGUAGE_TAG = 'nb-NO';

const shoppingCartStore = useShoppingCartStore();
const deliveryInformationStore = useDeliveryInformationStore();
const customerInformationStore = useCustomerInformationStore();
const checkoutStateStore = useCheckoutStateStore();
const netsEasyCheckoutStore = useNetsEasyCheckoutStore();
const paymentContainer = ref<HTMLDivElement | null>(null);
const checkout = ref<Dibs.Checkout | null>(null);

const props = defineProps<{
  isActive?: boolean;
}>();

const setupEventHandlers = () => {
  checkout.value?.on("pay-initialized", async (paymentId) => {
    checkout.value?.send("payment-order-finalized", true);
  });

  checkout.value?.on('payment-completed', async (response) => {
    // Swap to the full-view "placing order" loading stage before clearing the
    // cart - clearing it synchronously can tear down cart-dependent UI, and
    // `window.location.href` below doesn't navigate away instantly.
    checkoutStateStore.setCheckoutStage(CheckoutStages.PlacingOrder);

    shoppingCartStore.clear();
    deliveryInformationStore.clear();
    customerInformationStore.clear();
    checkoutStateStore.setPaymentId('');

    if (!response.paymentId) {
      Log({
        LogLevel: 'Error',
        Message: `[paymentInformationForm] Payment completed without a paymentId`,
      });
    } else {
      Log({
        LogLevel: 'Information',
        Message: `[paymentInformationForm] Payment completed with paymentId: "${response.paymentId}"`,
      });
    }

    await delayForOrderPlacementTesting();

    window.location.href = `${checkoutStateStore.receiptPageUrl}?paymentId=${response.paymentId}`;
  });
};

onMounted(async () => {
  let paymentId = checkoutStateStore.paymentId;

  if (!paymentId) {
    try {
      const data = await createPayment();
      if (data.paymentId) paymentId = data?.paymentId;
    } catch (error) {
      Log({
        LogLevel: 'Error',
        Message: `[paymentInformationForm] Could not create payment: ${error}`,
      });
    }
  }

  const checkoutOptions = {
    checkoutKey: checkoutStateStore.netsEasyApiCheckoutKey,
    paymentId: paymentId,
    containerId: paymentContainer.value?.id,
    language: LANGUAGE_TAG,
  };

  checkout.value = new window.Dibs!.Checkout(checkoutOptions);
  setupEventHandlers();
});

onUnmounted(() => {
  netsEasyCheckoutStore.checkout?.cleanup();
});
</script>

<template>
  <div class="checkout-payment">
    <div ref="paymentContainer" id="paymentContainer"></div>
  </div>
</template>
